import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@heroui/react';
import {
  Settings,
  LogOut,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Users,
  Shield,
  Lock,
  ScrollText,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useModuleStore } from '@/stores/moduleStore';
import {
  MODULE_NAV,
  type NavGroup,
} from '@/config/modules';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import MobileShell from './MobileShell';
import TopBar from './TopBar';

const ADMIN_GROUP: NavGroup = {
  label: 'Administration',
  defaultOpen: false,
  items: [
    { label: 'Users', icon: <Users size={18} />, path: '/admin/users' },
    { label: 'Roles', icon: <Shield size={18} />, path: '/admin/roles' },
    { label: 'Permissions', icon: <Lock size={18} />, path: '/admin/permissions' },
    { label: 'Activity Logs', icon: <ScrollText size={18} />, path: '/admin/activity-logs' },
  ],
};

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { activeModule } = useModuleStore();
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const { isMobile } = useBreakpoint();

  const navGroups = useMemo(() => [...MODULE_NAV[activeModule], ADMIN_GROUP], [activeModule]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    navGroups.forEach((g) => {
      if (g.label) initial[g.label] = g.defaultOpen ?? false;
    });
    return initial;
  });

  // Reset open groups when module changes
  useEffect(() => {
    const initial: Record<string, boolean> = {};
    navGroups.forEach((g) => {
      if (g.label) initial[g.label] = g.defaultOpen ?? false;
    });
    setOpenGroups(initial);
  }, [activeModule]);

  useEffect(() => {
    fetch('/api/v1/version')
      .then((r) => r.json())
      .then((data) => setServerVersion(data.commit))
      .catch(() => setServerVersion('?'));
  }, []);

  // Auto-open group that contains current path
  useEffect(() => {
    navGroups.forEach((g) => {
      if (g.label && g.items.some((item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/'))) {
        setOpenGroups((prev) => ({ ...prev, [g.label]: true }));
      }
    });
  }, [location.pathname, navGroups]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/projects') return location.pathname === '/projects';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const userInitials = user?.name
    ? user.name
        .split(' ')
        .slice(0, 2)
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : '??';

  if (isMobile) return <MobileShell />;

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: 'var(--bg-base)' }}>
      {/* ─── Module Tab Bar ──────────────────────────────────────── */}
      <TopBar />

      <div className="flex flex-1 overflow-hidden">
        {/* ─── Sidebar ─────────────────────────────────────────────── */}
        <aside
          className="flex flex-col border-r border-[var(--border)] transition-all duration-200 shrink-0"
          style={{
            backgroundColor: 'var(--bg-surface)',
            width: collapsed ? 56 : 220,
          }}
        >
          {/* Collapse toggle */}
          <div className="flex items-center justify-end h-10 px-3 border-b border-[var(--border)]">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1 rounded hover:bg-[var(--bg-panel)] transition-colors"
              style={{ color: 'var(--text-secondary)' }}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-2">
            {navGroups.map((group) => (
              <div key={group.label || '_top'} className="mb-1">
                {/* Group header (collapsible) */}
                {group.label && !collapsed && (
                  <button
                    onClick={() => toggleGroup(group.label)}
                    className="flex items-center justify-between w-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider font-ui hover:bg-[var(--bg-panel)] transition-colors"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {group.label}
                    {openGroups[group.label] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                )}

                {/* Items */}
                {(!group.label || openGroups[group.label] || collapsed) && (
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          title={collapsed ? item.label : undefined}
                          className={`
                            flex items-center gap-2.5 w-full text-left text-sm font-ui transition-colors
                            ${collapsed ? 'justify-center px-0 py-2' : 'px-3 py-1.5'}
                            ${active
                              ? 'bg-[var(--accent)]/10 font-medium'
                              : 'hover:bg-[var(--bg-panel)]'
                            }
                          `}
                          style={{
                            color: active ? 'var(--accent)' : 'var(--text-primary)',
                          }}
                        >
                          {item.icon}
                          {!collapsed && item.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* User section at bottom */}
          <div className="border-t border-[var(--border)] p-2">
            <Dropdown placement="top-start">
              <DropdownTrigger>
                <button
                  className={`flex items-center gap-2 w-full rounded-lg p-1.5 hover:bg-[var(--bg-panel)] transition-colors ${
                    collapsed ? 'justify-center' : ''
                  }`}
                >
                  <Avatar
                    size="sm"
                    name={userInitials}
                    classNames={{
                      base: 'cursor-pointer border border-[var(--border-em)] shrink-0',
                      name: 'text-xs font-ui',
                    }}
                    style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--accent)' }}
                  />
                  {!collapsed && (
                    <div className="flex flex-col items-start min-w-0">
                      <span className="text-xs font-medium truncate w-full" style={{ color: 'var(--text-primary)' }}>
                        {user?.name ?? 'Unknown'}
                      </span>
                      <span className="text-[10px] truncate w-full" style={{ color: 'var(--text-secondary)' }}>
                        {user?.email ?? ''}
                      </span>
                    </div>
                  )}
                </button>
              </DropdownTrigger>

              <DropdownMenu
                aria-label="User menu"
                variant="flat"
                classNames={{
                  base: 'border border-[var(--border)]',
                }}
                style={{ backgroundColor: 'var(--bg-panel)' }}
              >
                <DropdownItem
                  key="profile"
                  startContent={<User size={14} />}
                  textValue="Profile"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Profile
                </DropdownItem>

                <DropdownItem
                  key="change-password"
                  startContent={<Lock size={14} />}
                  textValue="Change Password"
                  style={{ color: 'var(--text-primary)' }}
                  onPress={() => navigate('/change-password')}
                >
                  Change Password
                </DropdownItem>

                <DropdownItem
                  key="settings"
                  startContent={<Settings size={14} />}
                  textValue="Settings"
                  style={{ color: 'var(--text-primary)' }}
                  onPress={() => navigate('/settings')}
                >
                  Settings
                </DropdownItem>

                <DropdownItem
                  key="logout"
                  color="danger"
                  startContent={<LogOut size={14} />}
                  onPress={handleLogout}
                  textValue="Log out"
                >
                  Log out
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* Version footer */}
          <div
            className="px-2 py-1 text-[9px] font-mono select-all border-t border-[var(--border)]"
            style={{ color: 'var(--text-secondary)', opacity: 0.4 }}
          >
            {collapsed ? 'v' : `c:${__APP_VERSION__} · s:${serverVersion ?? '…'}`}
          </div>
        </aside>

        {/* ─── Main Content ─────────────────────────────────────────── */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
